import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AdminNav } from './AdminNav';
import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { copy } from '../../config/copy';
import { useFormErrors } from '../auth/useFormErrors';
import { useCan, useSession } from '../../stores/session';

/**
 * Points per earned boost.
 *
 * Mirrored from `DiscoveryCredits::THRESHOLD` rather than fetched: this screen
 * only uses it to label the shortcut buttons, and a round trip so an admin
 * panel can print "100" would be silly. The server is still the authority on
 * what a grant actually produces, and says so in its response.
 */
const THRESHOLD = 100;

interface AdminUserDetail {
  user: {
    id: number;
    username: string;
    display_name: string;
    email: string;
    status: string;
    email_verified: boolean;
    email_status: string;
    created_at: string;
    last_seen_at: string | null;
    avatar: string | null;
    bio: string | null;
    location: string | null;
    follower_count: number;
    following_count: number;
  };
  roles: string[];
  available_roles: { name: string; label: string; description: string | null; is_system: boolean }[];
  /*
   * Named rather than an open record. `Record<string, number>` makes every
   * lookup `number | undefined` under `noUncheckedIndexedAccess`, and the six
   * keys are fixed by the one query in `ShowUserAction::counts()`.
   */
  counts: {
    tracks: number;
    tracks_live: number;
    tracks_held: number;
    comments: number;
    collections: number;
    plays: number;
  };
  host_applications: {
    id: number;
    status: string;
    country: string;
    created_at: string;
    decided_at: string | null;
    decision_note: string | null;
    reviewer: string | null;
  }[];
  /*
   * All three are absent rather than empty when the viewer lacks the
   * permission, so the page can leave the section out entirely. "No notes" and
   * "you may not read notes" are different facts and a blank panel shows both
   * the same way.
   */
  notes?: {
    id: number;
    body: string | null;
    /** Null once the author's account is gone - see the user_notes migration. */
    author: string | null;
    created_at: string;
    deleted_at: string | null;
    deleted_by: string | null;
  }[];
  reports?: {
    about_them: number;
    about_their_content: number;
    recent: {
      id: number;
      reason: string;
      status: string;
      created_at: string;
      subject_type: string;
      subject_id: number;
    }[];
  };
  history?: {
    id: number;
    action: string;
    created_at: string;
    subject_type: string;
    subject_id: number | null;
    actor: string | null;
  }[];
}

/**
 * One account, everything about it, every action on it.
 *
 * ## Why this exists
 *
 * The users list had two buttons: suspend, and a hardcoded "Make mod". That
 * shape has one bug per role - there was no way to revoke hosting from the
 * admin panel at all, because nobody had added a button for it - and no way to
 * see *why* a decision had been made before making the next one.
 *
 * A list is for finding somebody. This is for deciding about them, and it holds
 * the five things that decision turns on: what they have posted, what has been
 * reported, what roles they hold, what staff have said before, and what has
 * already been done to them.
 *
 * The roles here are enumerated from the server's own table, so a role added
 * next month appears without anybody touching this file.
 */
export function AdminUserPage() {
  const { id } = useParams();
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();

  const canAssignRoles = useCan('user.role.assign');
  const canSuspend = useCan('user.suspend');
  const canNote = useCan('user.note');
  const canAward = useCan('discovery.award');

  const key = ['admin', 'user', id];

  const { data, isPending, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<AdminUserDetail>(`/admin/users/${id}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    // The list's badges are derived from the same facts.
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const suspend = useMutation({
    mutationFn: (on: boolean) =>
      on ? api.post(`/admin/users/${id}/suspend`) : api.delete(`/admin/users/${id}/suspend`),
    onSuccess: invalidate,
  });

  const role = useMutation({
    mutationFn: ({ name, grant }: { name: string; grant: boolean }) =>
      grant
        ? api.post(`/admin/users/${id}/roles`, { role: name })
        : api.delete(`/admin/users/${id}/roles`, { body: { role: name } }),
    onSuccess: invalidate,
  });

  if (isPending) {
    return (
      <Frame>
        <LoadingPanel />
      </Frame>
    );
  }

  if (error || !data) {
    return (
      <Frame>
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'Could not load that account.'}
        </Alert>
      </Frame>
    );
  }

  const { user } = data;
  const isSelf = user.id === me?.id;
  /*
   * The server's message, verbatim, for both mutations.
   *
   * These are the deliberate refusals - "that is the only active
   * administrator" - and rewording them here would mean maintaining the same
   * reasoning in two places and getting it wrong in one.
   */
  const failure = (suspend.error ?? role.error) as ApiError | null;

  return (
    <Frame>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar src={user.avatar} name={user.display_name} size={56} />
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight text-ink">{user.display_name}</h1>
              {isSelf && <Chip>you</Chip>}
            </div>
            <Link to={`/${user.username}`} className="font-mono text-sm text-faint hover:text-soft">
              @{user.username}
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {user.status !== 'active' && <Chip tone="hot">{user.status}</Chip>}
          {!user.email_verified && <Chip tone="hot">unverified</Chip>}
          {/* A bounced or complained address is why their mail stops arriving,
              and it is the first thing to check when somebody says they never
              got the link. */}
          {(user.email_status === 'invalid' || user.email_status === 'complained') && (
            <Chip tone="hot">email {user.email_status}</Chip>
          )}
          {data.roles.map((name) => (
            <Chip key={name} tone="signal">
              {name}
            </Chip>
          ))}
        </div>
      </div>

      {failure && <Alert tone="error">{failure.message}</Alert>}

      {/* ------------------------------------------------------------------ */}
      {/* Account                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Card className="grid gap-4 p-5 sm:grid-cols-2">
        <Detail label="Email" value={user.email} mono />
        <Detail label="Joined" value={fullDate(user.created_at)} />
        <Detail label="Last seen" value={user.last_seen_at ? fullDate(user.last_seen_at) : 'Never'} />
        <Detail label="Location" value={user.location ?? '—'} />
        <Detail label="Followers" value={user.follower_count.toLocaleString()} />
        <Detail label="Following" value={user.following_count.toLocaleString()} />
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Tracks" value={data.counts.tracks} />
        <Stat label="Live" value={data.counts.tracks_live} />
        <Stat label="On hold" value={data.counts.tracks_held} tone={data.counts.tracks_held > 0} />
        <Stat label="Albums" value={data.counts.collections} />
        <Stat label="Comments" value={data.counts.comments} />
        <Stat label="Plays" value={data.counts.plays} />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Roles                                                               */}
      {/* ------------------------------------------------------------------ */}
      {canAssignRoles && (
        <Section title="Roles">
          <ul className="flex flex-col gap-2" data-roles>
            {data.available_roles.map((available) => {
              const held = data.roles.includes(available.name);

              return (
                <li
                  key={available.name}
                  data-role={available.name}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-control)] border border-line p-3"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-ink">{available.label}</span>
                    {available.description && (
                      <span className="text-xs text-faint">{available.description}</span>
                    )}
                  </div>

                  <Button
                    variant={held ? 'ghost' : 'secondary'}
                    loading={role.isPending && role.variables?.name === available.name}
                    onClick={() => role.mutate({ name: available.name, grant: !held })}
                    data-toggle-role={available.name}
                  >
                    {held ? 'Revoke' : 'Grant'}
                  </Button>
                </li>
              );
            })}
          </ul>

          {/* Said here because it is the one role change the person on the other
              end finds out about, and a moderator should know that before
              pressing it. */}
          {data.roles.includes('host') && (
            <p className="mt-3 text-xs text-faint">
              Revoking <span className="font-mono">host</span> notifies them. Their application stays
              on record, so it can be granted back without a new one.
            </p>
          )}
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Suspension                                                          */}
      {/* ------------------------------------------------------------------ */}
      {canSuspend && (
        <Section title="Account status">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={user.status === 'active' ? 'danger' : 'secondary'}
              // Disabled rather than hidden: the server refuses this too, and a
              // button that vanishes is more confusing than one visibly
              // unavailable.
              disabled={isSelf}
              loading={suspend.isPending}
              onClick={() => suspend.mutate(user.status === 'active')}
              data-suspend
            >
              {user.status === 'active' ? 'Suspend account' : 'Reinstate account'}
            </Button>
            <p className="text-xs text-faint">
              {isSelf
                ? 'You cannot suspend your own account.'
                : user.status === 'active'
                  ? 'Signs them out, hides their tracks, and stops them signing back in.'
                  : 'Restores their access and puts their tracks back.'}
            </p>
          </div>
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Hosting                                                             */}
      {/* ------------------------------------------------------------------ */}
      {data.host_applications.length > 0 && (
        <Section title="Host applications">
          <ul className="flex flex-col gap-2">
            {data.host_applications.map((application) => (
              <li
                key={application.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--radius-control)] border border-line p-3 text-sm"
              >
                <Chip tone={application.status === 'approved' ? 'fresh' : 'default'}>
                  {application.status}
                </Chip>
                <span className="text-soft">{shortDate(application.created_at)}</span>
                {application.reviewer && (
                  <span className="text-faint">by @{application.reviewer}</span>
                )}
                {application.decision_note && (
                  <p className="w-full text-xs text-faint">“{application.decision_note}”</p>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Reports                                                             */}
      {/* ------------------------------------------------------------------ */}
      {data.reports && (
        <Section title="Reports">
          <p className="text-sm text-soft">
            {data.reports.about_them} about this account, {data.reports.about_their_content} about
            things they posted.
          </p>

          {data.reports.recent.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {data.reports.recent.map((report) => (
                <li key={report.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <Chip tone={report.status === 'open' ? 'hot' : 'default'}>{report.status}</Chip>
                  <span className="text-ink">{report.reason}</span>
                  <span className="font-mono text-xs text-faint">
                    {report.subject_type} #{report.subject_id}
                  </span>
                  <span className="text-xs text-faint">{shortDate(report.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Notes                                                               */}
      {/* ------------------------------------------------------------------ */}
      {canAward && <AwardCredit userId={user.id} onChange={invalidate} />}

      {canNote && data.notes && <Notes userId={user.id} notes={data.notes} onChange={invalidate} />}

      {/* ------------------------------------------------------------------ */}
      {/* History                                                             */}
      {/* ------------------------------------------------------------------ */}
      {data.history && data.history.length > 0 && (
        <Section title="What has been done">
          <ul className="flex flex-col gap-1.5" data-history>
            {data.history.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono text-xs text-accent">{entry.action}</span>
                <span className="text-faint">
                  {entry.actor ? `by @${entry.actor}` : 'from the command line'}
                </span>
                <span className="text-xs text-faint">{shortDate(entry.created_at)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </Frame>
  );
}

/**
 * Granting discovery credit by hand.
 *
 * ## Why the reason is required here and optional everywhere else
 *
 * This is the only control on the panel that creates something rather than
 * restricting it, and "why" is the one part of it a later reader cannot work
 * out for themselves. A suspension explains itself from the account it is on;
 * a hundred points appearing in somebody's ledger does not.
 *
 * The server insists too - this is not a client-side courtesy.
 *
 * ## Points, not boosts
 *
 * The moderator grants the same currency everybody earns and the threshold does
 * the rest, so a grant that crosses it produces a boost through exactly the
 * path a real one does. The response says how many it produced, because that is
 * the only part the moderator cannot work out from the number they typed.
 */
function AwardCredit({ userId, onChange }: { userId: number; onChange: () => void }) {
  const [points, setPoints] = useState(100);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const errors = useFormErrors();

  const award = useMutation({
    mutationFn: () =>
      api.post<{ points: number; available: number; boosts_granted: number }>(
        `/admin/users/${userId}/discovery-credits`,
        { points, note: note.trim() },
      ),
    onError: errors.capture,
    onSuccess: (data) => {
      setNote('');
      setResult(
        data.boosts_granted > 0
          ? `Awarded. That crossed the threshold — ${data.boosts_granted} ${
              data.boosts_granted === 1 ? 'boost' : 'boosts'
            } granted, ${data.points} points total.`
          : `Awarded. ${data.points} points total.`,
      );
      onChange();
    },
  });

  return (
    <Section title={`Award ${copy['noun.boost.earned.plural'].toLowerCase()}`}>
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-relaxed text-soft">
          Points, not boosts — {THRESHOLD} of them becomes one, the same way an earned boost does.
          This is for putting right what the automatic rules could not.
        </p>

        <div className="flex flex-wrap items-center gap-1.5">
          {[10, 50, 100, 200, 300].map((amount) => (
            <button
              key={amount}
              type="button"
              aria-pressed={points === amount}
              data-award-points={amount}
              onClick={() => setPoints(amount)}
              className={`rounded-[var(--radius-pill)] border px-3.5 py-1.5 font-mono text-[11px] transition-colors ${
                points === amount
                  ? 'border-transparent bg-accent text-accent-ink'
                  : 'border-line text-soft hover:border-line-strong hover:text-ink'
              }`}
            >
              {amount}
              {amount % THRESHOLD === 0 && (
                <span className="ml-1.5 opacity-60">
                  = {amount / THRESHOLD}
                </span>
              )}
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-soft">Why (required, and recorded)</span>
          <input
            value={note}
            maxLength={200}
            data-award-note
            placeholder="Their room dropped mid-set and ten submissions were lost."
            onChange={(event) => setNote(event.target.value)}
            className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-line-strong"
          />
        </label>

        {errors.fields.points && <Alert tone="error">{errors.fields.points}</Alert>}
        {errors.fields.note && <Alert tone="error">{errors.fields.note}</Alert>}
        {result && <Alert tone="success">{result}</Alert>}

        <div>
          <Button
            loading={award.isPending}
            disabled={note.trim() === ''}
            data-award-submit
            onClick={() => {
              setResult(null);
              errors.clear();
              award.mutate();
            }}
          >
            Award {points} points
          </Button>
        </div>
      </div>
    </Section>
  );
}

/**
 * Staff notes.
 *
 * ## No edit control, on purpose
 *
 * A note that can be rewritten after the fact is not a record of what was known
 * at the time, and the next moderator reading one has no way to tell which they
 * are looking at. Wrong notes get deleted and replaced, which leaves both facts
 * visible.
 *
 * Deleted notes keep their row and lose their body, so "somebody removed a note
 * here" is something the next reader can see.
 */
function Notes({
  userId,
  notes,
  onChange,
}: {
  userId: number;
  notes: NonNullable<AdminUserDetail['notes']>;
  onChange: () => void;
}) {
  const [body, setBody] = useState('');

  const add = useMutation({
    mutationFn: () => api.post(`/admin/users/${userId}/notes`, { body: body.trim() }),
    onSuccess: () => {
      setBody('');
      onChange();
    },
  });

  const remove = useMutation({
    mutationFn: (noteId: number) => api.delete(`/admin/users/${userId}/notes/${noteId}`),
    onSuccess: onChange,
  });

  return (
    <Section title="Staff notes">
      <p className="text-xs text-faint">
        Only staff see these. They cannot be edited — delete and rewrite instead — and they are
        removed with the account.
      </p>

      <div className="mt-3 flex flex-col gap-2">
        <textarea
          value={body}
          maxLength={2000}
          data-note-body
          onChange={(event) => setBody(event.target.value)}
          placeholder="What happened, and what was decided."
          className="min-h-20 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
        />
        <div className="flex items-center gap-3">
          <Button
            loading={add.isPending}
            disabled={body.trim() === ''}
            onClick={() => add.mutate()}
            data-add-note
          >
            Add note
          </Button>
          {add.isError && (
            <span className="text-sm text-hot-text">
              {add.error instanceof ApiError ? add.error.message : 'That could not be saved.'}
            </span>
          )}
        </div>
      </div>

      {notes.length > 0 && (
        <ul className="mt-5 flex flex-col gap-3" data-notes>
          {notes.map((note) => (
            <li
              key={note.id}
              data-note={note.id}
              className="rounded-[var(--radius-control)] border border-line p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-faint">
                  {note.author ? `@${note.author}` : 'account removed'} ·{' '}
                  {fullDate(note.created_at)}
                </span>
                {note.deleted_at === null && (
                  <button
                    type="button"
                    data-delete-note
                    onClick={() => remove.mutate(note.id)}
                    className="text-xs text-faint transition-colors hover:text-hot-text"
                  >
                    Delete
                  </button>
                )}
              </div>

              {note.body === null ? (
                <p className="mt-1.5 text-sm italic text-faint">
                  Note removed{note.deleted_by ? ` by @${note.deleted_by}` : ''}.
                </p>
              ) : (
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-soft">{note.body}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-5 px-6 py-10">
      <AdminNav />
      <Link to="/admin" className="text-sm text-soft hover:text-ink">
        ← All accounts
      </Link>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="meta-caps mb-3 text-faint">{title}</h2>
      <Card className="p-5">{children}</Card>
    </section>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="meta-caps text-faint">{label}</span>
      <span className={`truncate text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`} title={value}>
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value, tone = false }: { label: string; value: number; tone?: boolean }) {
  return (
    <Card className="flex flex-col gap-1 p-3">
      <span className="meta-caps text-faint">{label}</span>
      <span className={`font-mono text-lg ${tone ? 'text-hot-text' : 'text-ink'}`}>
        {value.toLocaleString()}
      </span>
    </Card>
  );
}

function shortDate(iso: string): string {
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fullDate(iso: string): string {
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
